import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { ChefHat, Utensils, Wheat, Flame, Award, Clock, Star, Citrus } from 'lucide-react';

const CulinaryArts: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "L'École de Cuisine";
    
    return (
        <div className="min-h-screen bg-stone-50 font-serif text-stone-900 selection:bg-orange-100 selection:text-orange-900">
            {/* Top Reserve Bar */}
            <div className="bg-stone-900 text-stone-400 py-3 px-8 text-[10px] font-sans font-bold uppercase tracking-[0.2em] flex justify-between items-center">
                <span>Paris • London • New York</span>
                <div className="flex gap-6">
                    <a href="#" className="hover:text-white transition-colors">Restaurant Reservations</a>
                    <a href="#" className="hover:text-white transition-colors">Masterclasses</a>
                    <a href="#" className="hover:text-white transition-colors">Boutique</a>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-stone-200">
                <div className="max-w-7xl mx-auto px-8 h-28 flex justify-between items-center">
                    <div className="flex flex-col items-center">
                         <div className="flex items-center gap-2 mb-1">
                             <ChefHat size={24} strokeWidth={1} className="text-orange-900" />
                         </div>
                         <span className="font-bold text-xl uppercase tracking-widest text-stone-900 leading-none">{schoolName}</span>
                         <span className="text-[10px] font-sans font-medium text-stone-500 uppercase tracking-[0.4em] mt-1">Instituted 1954</span>
                    </div>

                    <div className="hidden md:flex items-center gap-12 font-sans text-xs font-bold text-stone-500 uppercase tracking-widest">
                        <a href="#" className="hover:text-stone-900 transition-colors">Programs</a>
                        <a href="#" className="hover:text-stone-900 transition-colors">Chefs</a>
                        <a href="#" className="hover:text-stone-900 transition-colors">Admissions</a>
                        <a href="#" className="hover:text-stone-900 transition-colors">Campus</a>
                    </div>

                    <Button className="font-sans bg-stone-900 hover:bg-stone-800 text-white font-bold uppercase tracking-widest text-xs h-10 px-8 rounded-none">
                        Apply
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative pt-24 pb-32 px-8 overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    <div className="order-2 lg:order-1 relative z-10">
                        <span className="block font-sans text-xs text-orange-700 font-bold uppercase tracking-[0.2em] mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            Excellence in Gastronomy
                        </span>
                        
                        <h1 className="text-6xl md:text-8xl font-normal text-stone-900 leading-[1.1] mb-8">
                            The Art of <br/>
                            <span className="italic font-light text-stone-500">Fine Dining.</span>
                        </h1>
                        
                        <p className="font-sans text-stone-600 max-w-md mb-12 leading-loose text-sm tracking-wide">
                            Master the techniques of French culinary tradition. Learn from world-renowned Michelin-star chefs in state-of-the-art kitchens.
                        </p>
                        
                        <div className="flex gap-4">
                            <Button className="bg-orange-900 hover:bg-orange-800 text-white font-sans font-bold uppercase tracking-widest text-xs h-14 px-8 rounded-none">
                                View Diplomas
                            </Button>
                            <Button variant="outline" className="border-stone-300 text-stone-600 hover:text-stone-900 font-sans font-bold uppercase tracking-widest text-xs h-14 px-8 rounded-none bg-transparent">
                                Discover
                            </Button>
                        </div>
                    </div>

                    {/* Hero Image Collage */}
                    <div className="order-1 lg:order-2 relative h-[600px] w-full">
                         <div className="absolute top-0 right-0 w-3/4 h-3/4 bg-stone-200 overflow-hidden">
                            <img 
                                src="https://images.unsplash.com/photo-1556910103-1c02745a30bf?q=80&w=2070&auto=format&fit=crop" 
                                alt="Chef Plating" 
                                className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-1000 grayscale hover:grayscale-0"
                            />
                         </div>
                         <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-stone-300 p-2 shadow-2xl overflow-hidden">
                             <div className="w-full h-full border border-white/20 relative">
                                <img 
                                    src="https://images.unsplash.com/photo-1514326640560-7d063ef2aed5?q=80&w=2080&auto=format&fit=crop" 
                                    alt="Dish Detail" 
                                    className="w-full h-full object-cover"
                                />
                             </div>
                         </div>
                         {/* Stamp Graphic */}
                         <div className="absolute top-[60%] right-[10%] w-32 h-32 rounded-full border border-stone-900 flex items-center justify-center animate-spin-slow bg-white text-stone-900 text-[10px] uppercase font-bold tracking-widest p-4 text-center">
                             <div className="relative w-full h-full flex items-center justify-center">
                                 <Award size={32} className="absolute" />
                                 <svg viewBox="0 0 100 100" className="w-full h-full absolute inset-0 animate-spin-reverse">
                                     <path id="curve" d="M 50 50 m -37 0 a 37 37 0 1 1 74 0 a 37 37 0 1 1 -74 0" fill="transparent" />
                                     <text>
                                         <textPath href="#curve" className="fill-current">
                                             • Michelin Guide • Excellence •
                                         </textPath>
                                     </text>
                                 </svg>
                             </div>
                         </div>
                    </div>
                </div>
            </header>

            {/* Disciplines Section */}
            <section className="py-24 px-8 bg-white border-y border-stone-100">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-stone-100 border border-stone-100">
                        {[
                            { icon: Wheat, title: "Boulangerie", desc: "The art of bread making and fermentation." },
                            { icon: Citrus, title: "Pâtisserie", desc: "Complex pastries, sugar art, and chocolate." },
                            { icon: Utensils, title: "Cuisine", desc: "Classic techniques and modern innovation." },
                            { icon: Flame, title: "Management", desc: "Restaurant operations and wine stewardship." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-12 hover:bg-orange-50/30 transition-colors group cursor-pointer text-center md:text-left">
                                <item.icon strokeWidth={1} size={40} className="text-stone-400 mb-8 mx-auto md:mx-0 group-hover:text-orange-900 transition-colors" />
                                <h3 className="text-2xl font-normal text-stone-900 mb-4">{item.title}</h3>
                                <p className="font-sans text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-stone-900 text-stone-400 py-24 px-8">
                 <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
                     <ChefHat size={48} strokeWidth={1} className="text-white mb-8" />
                     <h2 className="text-3xl font-normal text-white mb-2">{schoolName}</h2>
                     <p className="font-sans text-xs uppercase tracking-[0.2em] mb-12">Le Cordon Bleu Global Alliance</p>
                     
                     <div className="flex flex-wrap justify-center gap-12 font-sans text-xs font-bold uppercase tracking-widest text-stone-500 mb-12">
                         <a href="#" className="hover:text-white transition-colors">Press Room</a>
                         <a href="#" className="hover:text-white transition-colors">Careers</a>
                         <a href="#" className="hover:text-white transition-colors">Alumni</a>
                         <a href="#" className="hover:text-white transition-colors">Contact</a>
                     </div>
                     
                     <p className="font-sans text-[10px] text-stone-700">
                         &copy; 2026 {schoolName}. All rights reserved.
                     </p>
                 </div>
            </footer>
        </div>
    );
};

export default CulinaryArts;
